import React from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Stack, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import moment from "moment";

import Modal from "components/basic/modal";
import Button from "components/basic/button";
import Typography from "components/basic/typography";
import { Booking } from "../../types";

interface DeleteBookingDialogProps {
  booking: Booking;
  onCancel: () => void;
  onConfirm: () => void;
}

const DeleteBookingDialog: React.FC<DeleteBookingDialogProps> = ({
  booking,
  onCancel,
  onConfirm,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("calendar");

  const when = booking.allDay
    ? `${moment(booking.date).format("MMM D, YYYY")} · All day`
    : `${moment(booking.date).format("MMM D, YYYY")} · ${moment(
        booking.from,
        "HH:mm",
      ).format("h:mm A")} – ${moment(booking.to, "HH:mm").format("h:mm A")}`;

  return (
    <Modal onClose={onCancel}>
      <Box sx={{ width: { xs: "82vw", sm: 380 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <ErrorOutlineIcon
            sx={{ color: theme.palette.warning.main, mt: 0.25 }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" style={{ fontWeight: 600 }}>
              {t("delete_booking.title", { title: booking.title })}
            </Typography>
            <Typography
              variant="body2"
              style={{
                marginTop: theme.spacing(1),
                color: theme.palette.text.secondary,
              }}
            >
              {when}
            </Typography>
            <Typography
              variant="body2"
              style={{
                marginTop: theme.spacing(1),
                color: theme.palette.text.secondary,
              }}
            >
              {t("delete_booking.body")}
            </Typography>
          </Box>
          <IconButton onClick={onCancel} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack
          direction="row"
          spacing={1.5}
          justifyContent="flex-end"
          sx={{ mt: 3 }}
        >
          <Button variant="outlined" color="primary" onClick={onCancel}>
            {t("delete_booking.cancel")}
          </Button>
          <Button variant="contained" color="error" onClick={onConfirm}>
            {t("delete_booking.confirm")}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default DeleteBookingDialog;
